import { GaugeIcon } from "lucide-react";
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";
import { formatMbps, requiredBitrateMbps } from "../lib/bitrate";

/**
 * Informacja o wymaganym łączu, pokazywana na czas wczytywania.
 *
 * Powód: materiał serwujemy jako plik, a nie jako drabinkę jakości — nie ma
 * przełączania na niższy wariant, gdy pasma brakuje. Odtwarzacz na wolnym łączu
 * po prostu **stoi i się buforuje**, co wygląda jak awaria serwera, choć jest
 * ograniczeniem po stronie widza. Podana liczba pozwala to rozpoznać w kilka
 * sekund zamiast zgłaszać błąd.
 *
 * Kilka sekund, nie na stałe: to informacja przydatna raz, przy starcie.
 * Wisząca stale nad obrazem byłaby elementem interfejsu, który wyłącznie
 * przeszkadza.
 */

/** Jak długo widać podpowiedź. Dość, żeby przeczytać, za mało, żeby zawadzać. */
const VISIBLE_MS = 7000;

export function BandwidthHint({
  videoRef,
  sizeBytes,
  durationSec,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  sizeBytes: number | null;
  durationSec: number | null;
}) {
  const mbps = requiredBitrateMbps(sizeBytes, durationSec);

  const [visible, setVisible] = useState(false);
  const [buffering, setBuffering] = useState(false);

  /**
   * Odliczanie trzymamy w ref i restartujemy jawnie.
   *
   * Wcześniej był tu efekt zależny od licznika pokazów — czyli zależność, która
   * nic nie mówi o treści, tylko wymusza ponowne uruchomienie. Jawny timer robi
   * to samo i widać przy nim, co się dzieje.
   */
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback(() => {
    setVisible(true);
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setVisible(false), VISIBLE_MS);
  }, []);

  useEffect(() => {
    show();
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [show]);

  /**
   * Powtórzenie przy zacinaniu.
   *
   * Wtedy ta liczba jest najbardziej potrzebna: widz widzi, że obraz staje,
   * i dostaje wprost powód. Przewijanie ODRZUCAMY — `waiting` leci też po
   * każdym skoku na pasku, a to normalne dobieranie danych, nie brak pasma.
   */
  useEffect(() => {
    const video = videoRef.current;
    if (video === null || mbps === null) return;

    const onWaiting = () => {
      if (video.seeking) return;
      setBuffering(true);
      show();
    };
    const onPlaying = () => setBuffering(false);

    video.addEventListener("waiting", onWaiting);
    video.addEventListener("playing", onPlaying);
    return () => {
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("playing", onPlaying);
    };
  }, [videoRef, mbps, show]);

  // Bez rozmiaru albo czasu trwania nie ma czego policzyć, a liczba wzięta
  // z powietrza byłaby gorsza niż jej brak.
  if (mbps === null || !visible) return null;

  return (
    // `output`, nie `div role="status"`: element semantyczny niesie tę rolę sam
    // i sam zgłasza czytnikowi ekranu, że treść pojawiła się w trakcie.
    <output
      // `data-slot` niesie animację wejścia z `styles.css` — wtyczki animacji
      // tu nie ma, więc klasy `animate-in` z przykładów byłyby nazwami bez reguły.
      data-slot="player-hint"
      className="absolute top-3 left-3 z-10 flex max-w-[min(22rem,70%)] items-start gap-2 rounded-lg bg-background/80 px-3 py-2 text-sm shadow-lg backdrop-blur-sm"
    >
      <GaugeIcon className="mt-0.5 size-4 shrink-0 text-accent" />
      {buffering ? (
        <p className="text-muted-foreground">
          Obraz się zatrzymuje na dobieraniu danych. Ten plik wymaga <Rate mbps={mbps} /> — sprawdź
          łącze.
        </p>
      ) : (
        <p className="text-muted-foreground">
          Do płynnego strumieniowania tego pliku potrzebujesz łącza co najmniej <Rate mbps={mbps} />
          .
        </p>
      )}
    </output>
  );
}

function Rate({ mbps }: { mbps: number }) {
  return <span className="font-medium text-foreground tabular-nums">{formatMbps(mbps)} Mb/s</span>;
}
